import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { MagnifyingGlass, X, UserPlus, Star } from "@phosphor-icons/react";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";

const inputCls =
  "w-full bg-transparent border-b py-2 outline-none focus:border-[#C2A878] text-sm";
const btnGold =
  "px-3 py-2 text-[10px] uppercase tracking-[0.15em] border transition-colors";

/**
 * CustomerPicker — enhances the POS customer selector.
 * - Type-ahead search by name / phone / email (calls /api/customers/search).
 * - "+ Create new customer" inline flow with phone-duplicate detection.
 * - Keeps Walk-in as first-class option.
 */
export default function CustomerPicker({
  selected,          // customer object | null
  walkIn = true,     // fallback if not selected
  onSelect,          // (customer | null) => void
  disabled = false,
  onCustomerCreated, // optional (customer) => void — for parent-side cache refresh
}) {
  const { t } = useI18n();
  const { user } = useAuth();
  const canCreate = (user?.permissions || []).includes("customer:create") || user?.role === "admin";

  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [initialCreate, setInitialCreate] = useState(null);
  const boxRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  // Debounced search
  useEffect(() => {
    clearTimeout(debounceRef.current);
    const trimmed = q.trim();
    if (trimmed.length < 2) {
      setRows([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api.get(`/customers/search`, { params: { q: trimmed } });
        setRows(r.data || []);
      } catch (e) {
        if (e?.response?.status !== 403) toast.error(formatApiError(e));
        setRows([]);
      } finally {
        setLoading(false);
      }
    }, 220);
    return () => clearTimeout(debounceRef.current);
  }, [q]);

  const openPanel = useCallback(() => {
    if (disabled) return;
    setOpen(true);
    setTimeout(() => inputRef.current?.focus(), 30);
  }, [disabled]);

  const pick = (c) => {
    onSelect(c);
    setOpen(false);
    setQ("");
    setRows([]);
  };

  const clear = () => {
    onSelect(null);
    setOpen(false);
    setQ("");
    setRows([]);
  };

  const openCreate = () => {
    setInitialCreate(guessInitial(q));
    setCreateOpen(true);
  };

  return (
    <div className="relative" ref={boxRef} data-testid="pos-customer-picker">
      {/* Selected chip OR search entry */}
      {selected ? (
        <div
          className="flex items-start justify-between gap-2 border p-3"
          style={{ borderColor: "var(--nylsu-border)" }}
          data-testid="pos-customer-selected"
        >
          <div className="min-w-0">
            <div className="font-serif text-sm truncate flex items-center gap-1">
              {selected.name}
              {selected.vip && (
                <Star size={12} weight="fill" style={{ color: "var(--nylsu-gold)" }} />
              )}
            </div>
            <div className="text-xs text-[#5C5A54] truncate">
              {[selected.phone, selected.email, selected.city].filter(Boolean).join(" · ") || "—"}
            </div>
          </div>
          <div className="flex flex-col gap-1 shrink-0">
            <button
              type="button"
              onClick={openPanel}
              disabled={disabled}
              className="text-[10px] uppercase tracking-[0.15em] underline text-[#5C5A54] hover:text-[#0A0A0A] disabled:opacity-40"
              data-testid="pos-customer-change"
            >
              {t("pos.customerChange")}
            </button>
            <button
              type="button"
              onClick={clear}
              disabled={disabled}
              className="text-[10px] uppercase tracking-[0.15em] underline text-[#8F8D84] hover:text-[#8C3B3B] disabled:opacity-40"
              data-testid="pos-customer-clear"
            >
              {t("pos.customerClear")}
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={openPanel}
          disabled={disabled}
          className="w-full flex items-center gap-2 border px-3 py-3 text-left hover:bg-[#F4F3ED] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          style={{ borderColor: "var(--nylsu-border)" }}
          data-testid="pos-customer-open"
        >
          <MagnifyingGlass size={16} weight="light" className="text-[#8F8D84]" />
          <div className="flex-1">
            <div className="text-sm">
              {walkIn ? t("pos.walkIn") : t("pos.customerSearch")}
            </div>
            <div className="text-[10px] text-[#8F8D84] uppercase tracking-[0.15em]">
              {t("pos.customerSearch")}
            </div>
          </div>
        </button>
      )}

      {/* Search panel */}
      {open && (
        <div
          className="absolute z-40 left-0 right-0 mt-2 border bg-white shadow-lg"
          style={{ borderColor: "var(--nylsu-border-strong)" }}
          data-testid="pos-customer-panel"
        >
          <div className="flex items-center gap-2 p-3 border-b" style={{ borderColor: "var(--nylsu-border)" }}>
            <MagnifyingGlass size={16} weight="light" className="text-[#8F8D84]" />
            <input
              ref={inputRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("pos.customerSearch")}
              className="flex-1 bg-transparent outline-none text-sm"
              data-testid="pos-customer-search-input"
            />
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="p-1 text-[#8F8D84] hover:text-[#0A0A0A]"
              aria-label="close"
            >
              <X size={14} weight="light" />
            </button>
          </div>

          <div className="max-h-[320px] overflow-y-auto">
            {q.trim().length < 2 ? (
              <div className="p-4 text-xs text-[#8F8D84]" data-testid="pos-customer-hint">
                {t("pos.customerSearchHint")}
              </div>
            ) : loading ? (
              <div className="p-4 text-xs text-[#8F8D84]">…</div>
            ) : rows.length === 0 ? (
              <div className="p-4 text-center" data-testid="pos-customer-empty">
                <div className="text-xs text-[#8F8D84] mb-3">{t("pos.customerNoResults")}</div>
                {canCreate && (
                  <button
                    type="button"
                    onClick={openCreate}
                    className={`${btnGold} inline-flex items-center gap-2`}
                    style={{ borderColor: "var(--nylsu-gold)", color: "var(--nylsu-gold)" }}
                    data-testid="pos-customer-create-empty"
                  >
                    <UserPlus size={12} weight="light" />
                    {t("pos.customerCreate")}
                  </button>
                )}
              </div>
            ) : (
              rows.map((c) => (
                <button
                  type="button"
                  key={c.id}
                  onClick={() => pick(c)}
                  className="w-full text-left px-3 py-2 border-b hover:bg-[#F4F3ED] transition-colors"
                  style={{ borderColor: "var(--nylsu-border)" }}
                  data-testid={`pos-customer-result-${c.id}`}
                >
                  <div className="flex items-center gap-1 font-serif text-sm">
                    {c.name}
                    {c.vip && <Star size={11} weight="fill" style={{ color: "var(--nylsu-gold)" }} />}
                  </div>
                  <div className="text-[11px] text-[#5C5A54]">
                    {[c.phone, c.email, c.city].filter(Boolean).join(" · ") || "—"}
                  </div>
                </button>
              ))
            )}
          </div>

          {rows.length > 0 && canCreate && (
            <div className="p-2 border-t" style={{ borderColor: "var(--nylsu-border)" }}>
              <button
                type="button"
                onClick={openCreate}
                className="w-full text-[10px] uppercase tracking-[0.15em] py-2 border hover:bg-[#F4F3ED]"
                style={{ borderColor: "var(--nylsu-border)" }}
                data-testid="pos-customer-create-inline"
              >
                {t("pos.customerCreate")}
              </button>
            </div>
          )}
        </div>
      )}

      {createOpen && (
        <NewCustomerModal
          initial={initialCreate}
          onClose={() => setCreateOpen(false)}
          onCreated={(c) => {
            onCustomerCreated?.(c);
            pick(c);
            setCreateOpen(false);
          }}
        />
      )}
    </div>
  );
}

function guessInitial(q) {
  const trimmed = (q || "").trim();
  if (!trimmed) return {};
  const digits = trimmed.replace(/\D+/g, "");
  if (digits.length >= 3 && digits.length >= trimmed.length - 4) {
    return { phone: trimmed };
  }
  return { name: trimmed };
}

function NewCustomerModal({ initial = {}, onClose, onCreated }) {
  const { t } = useI18n();
  const [form, setForm] = useState({
    name: initial.name || "",
    phone: initial.phone || "",
    email: "",
    city: "",
    type: "showroom",
  });
  const [duplicate, setDuplicate] = useState(null);
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Debounced duplicate check on phone
  useEffect(() => {
    const p = (form.phone || "").trim();
    if (!p || p.replace(/\D+/g, "").length < 6) {
      setDuplicate(null);
      return;
    }
    const h = setTimeout(async () => {
      setChecking(true);
      try {
        const r = await api.get("/customers/check-duplicate", { params: { phone: p } });
        setDuplicate(r.data?.duplicate || null);
      } catch {
        setDuplicate(null);
      } finally {
        setChecking(false);
      }
    }, 260);
    return () => clearTimeout(h);
  }, [form.phone]);

  const save = async (force = false) => {
    if (!form.name.trim()) {
      toast.error(t("pos.customerName"));
      return;
    }
    setSaving(true);
    try {
      const r = await api.post("/customers", form, { params: force ? { force: true } : {} });
      toast.success(t("pos.customerCreated"));
      onCreated(r.data);
    } catch (e) {
      // 409 duplicate — offer to select existing
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.existing) {
        setDuplicate(detail.existing);
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="pos-new-customer-modal"
    >
      <div
        className="w-full max-w-md bg-white border shadow-xl"
        style={{ borderColor: "var(--nylsu-border-strong)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="p-4 border-b flex items-center justify-between"
          style={{ borderColor: "var(--nylsu-border)" }}
        >
          <div className="overline">{t("pos.newCustomer")}</div>
          <button type="button" onClick={onClose} className="p-1 text-[#8F8D84] hover:text-[#0A0A0A]">
            <X size={14} weight="light" />
          </button>
        </div>
        <div className="p-4 space-y-4">
          <div>
            <label className="overline block mb-1">{t("pos.customerName")}</label>
            <input
              autoFocus
              className={inputCls}
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              data-testid="pos-new-customer-name"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="overline block mb-1">{t("pos.customerPhone")}</label>
              <input
                className={inputCls}
                value={form.phone}
                onChange={(e) => set("phone", e.target.value)}
                placeholder="+90 532 …"
                data-testid="pos-new-customer-phone"
              />
            </div>
            <div>
              <label className="overline block mb-1">{t("pos.customerCity")}</label>
              <input
                className={inputCls}
                value={form.city}
                onChange={(e) => set("city", e.target.value)}
                data-testid="pos-new-customer-city"
              />
            </div>
          </div>
          <div>
            <label className="overline block mb-1">{t("pos.customerEmail")}</label>
            <input
              type="email"
              className={inputCls}
              value={form.email}
              onChange={(e) => set("email", e.target.value)}
              data-testid="pos-new-customer-email"
            />
          </div>
          <div>
            <label className="overline block mb-1">{t("pos.customerType")}</label>
            <select
              className={inputCls}
              value={form.type}
              onChange={(e) => set("type", e.target.value)}
              data-testid="pos-new-customer-type"
            >
              {["retail", "showroom", "wholesale", "mto", "vip", "other"].map((v) => (
                <option key={v} value={v}>{v}</option>
              ))}
            </select>
          </div>

          {duplicate && (
            <div
              className="border p-3 text-xs"
              style={{ borderColor: "var(--nylsu-warning)", background: "#FFF8EA" }}
              data-testid="pos-new-customer-duplicate"
            >
              <div className="uppercase tracking-[0.15em] text-[10px] mb-2" style={{ color: "var(--nylsu-warning)" }}>
                {t("pos.customerDuplicate")}
              </div>
              <div className="font-serif text-sm">{duplicate.name}</div>
              <div className="text-[11px] text-[#5C5A54]">
                {[duplicate.phone, duplicate.email, duplicate.city].filter(Boolean).join(" · ")}
              </div>
              <div className="flex gap-2 mt-2">
                <button
                  type="button"
                  onClick={() => onCreated(duplicate)}
                  className={btnGold}
                  style={{ borderColor: "var(--nylsu-gold)", color: "var(--nylsu-gold)" }}
                  data-testid="pos-new-customer-select-duplicate"
                >
                  {t("pos.customerDuplicateSelect")}
                </button>
                <button
                  type="button"
                  onClick={() => save(true)}
                  disabled={saving}
                  className={btnGold}
                  style={{ borderColor: "var(--nylsu-border-strong)", color: "#5C5A54" }}
                  data-testid="pos-new-customer-force"
                >
                  {t("pos.customerDuplicateContinue")}
                </button>
              </div>
            </div>
          )}
        </div>
        <div
          className="p-4 border-t flex items-center justify-end gap-2"
          style={{ borderColor: "var(--nylsu-border)" }}
        >
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className={btnGold}
            style={{ borderColor: "var(--nylsu-border)", color: "#5C5A54" }}
          >
            {t("pos.customerCancel")}
          </button>
          <button
            type="button"
            onClick={() => save(false)}
            disabled={saving || checking}
            className={btnGold}
            style={{ background: "var(--nylsu-gold)", color: "#0A0A0A", borderColor: "var(--nylsu-gold)" }}
            data-testid="pos-new-customer-save"
          >
            {t("pos.customerSave")}
          </button>
        </div>
      </div>
    </div>
  );
}
